import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import backArrow from "../../assets/images/backArrow.svg";
import { buildSessionPath, getStoredSession } from "../mmtUserList/mmtFlow";
import { addPatient } from "../mmtUserList/mmtApi";
import { USER_LIST } from "../../routes/routerPath";
import { isWebView, postWebViewMessage } from "../../utlis/webViewBridge";
import "./testperson.scss";

const GENDERS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString("en", { month: "short" }),
}));

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 80 }, (_, i) => currentYear - 21 - i);

const isValidName = (s) => /^[\p{L}\p{M}\s'-]+$/u.test(s);

export default function MmtAddTestPerson({ onClose, onSuccess }) {
  const navigate = useNavigate();
  const [session] = useState(getStoredSession);
  const [step1, setStep1] = useState({
    firstname: "",
    lastname: "",
    testPersonId: "",
    gender: "",
    birthYear: "",
    birthMonth: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const setField = (key, value) =>
    setStep1((prev) => ({ ...prev, [key]: value }));

  const firstNameError =
    step1.firstname.trim() !== "" && !isValidName(step1.firstname);
  const lastNameError =
    step1.lastname.trim() !== "" && !isValidName(step1.lastname);
  const testIdError =
    step1.testPersonId !== "" && !/^[a-zA-Z0-9]+$/.test(step1.testPersonId);

  const isFormValid =
    step1.firstname.trim() !== "" &&
    !firstNameError &&
    step1.lastname.trim() !== "" &&
    !lastNameError &&
    step1.testPersonId.trim() !== "" &&
    !testIdError &&
    step1.gender !== "" &&
    step1.birthYear !== "" &&
    step1.birthMonth !== "";

  const back = () => {
    if (postWebViewMessage("BACK_TO_USER_LIST")) return;
    if (onClose) {
      onClose();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const done = () => {
    sessionStorage.removeItem("userList");
    if (onSuccess) {
      onSuccess();
      return;
    }
    navigate(buildSessionPath(USER_LIST, session), { replace: true });
  };

  const submit = async () => {
    if (!isFormValid || !session) return;
    setSaving(true);
    setError("");
    try {
      await addPatient(session, step1);
      if (!postWebViewMessage("TEST_PERSON_ADDED")) done();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to add test person.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!session && !isWebView()) return null;

  return (
    <main className="mmt-test-person-page">
      <header className="mmt-tp-header">
        <button onClick={back} aria-label="Back" className="mmt-tp-back">
          <img src={backArrow} alt="" />
        </button>
        <h1>Add Test Person</h1>
      </header>

      <section className="mmt-tp-form">
        {error ? <div className="mmt-tp-error">{error}</div> : null}

        <div className="mmt-field">
          <span>First Name *</span>
          <input
            value={step1.firstname}
            onChange={(e) => setField("firstname", e.target.value)}
            maxLength={50}
            className={firstNameError ? "mmt-input-error" : ""}
            disabled={saving}
          />
          {firstNameError ? (
            <em className="mmt-tp-hint">
              Only letters, spaces, hyphens, and apostrophes
            </em>
          ) : null}
        </div>

        <div className="mmt-field">
          <span>Last Name *</span>
          <input
            value={step1.lastname}
            onChange={(e) => setField("lastname", e.target.value)}
            maxLength={50}
            className={lastNameError ? "mmt-input-error" : ""}
            disabled={saving}
          />
          {lastNameError ? (
            <em className="mmt-tp-hint">
              Only letters, spaces, hyphens, and apostrophes
            </em>
          ) : null}
        </div>

        <div className="mmt-field">
          <span>Test Person ID *</span>
          <input
            value={step1.testPersonId}
            onChange={(e) => setField("testPersonId", e.target.value)}
            placeholder="Alphanumeric"
            maxLength={20}
            autoCapitalize="none"
            className={testIdError ? "mmt-input-error" : ""}
            disabled={saving}
          />
          {testIdError ? (
            <em className="mmt-tp-hint">Please enter alphanumeric value</em>
          ) : null}
        </div>

        <div className="mmt-field">
          <span>Gender *</span>
          <div className="mmt-tp-radio-group">
            {GENDERS.map((g) => (
              <button
                key={g.value}
                type="button"
                className={`mmt-tp-radio-btn${step1.gender === g.value ? " selected" : ""}`}
                onClick={() => setField("gender", g.value)}
                disabled={saving}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mmt-tp-row">
          <label className="mmt-field">
            <span>Birth Year *</span>
            <select
              value={step1.birthYear}
              onChange={(e) =>
                setField(
                  "birthYear",
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              disabled={saving}
            >
              <option value="">Year</option>
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>

          <label className="mmt-field">
            <span>Birth Month *</span>
            <select
              value={step1.birthMonth}
              onChange={(e) =>
                setField(
                  "birthMonth",
                  e.target.value ? Number(e.target.value) : "",
                )
              }
              disabled={saving}
            >
              <option value="">Month</option>
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mmt-tp-actions">
          <button
            type="button"
            className="mmt-primary-button"
            onClick={submit}
            disabled={!isFormValid || saving}
          >
            {saving ? "Saving..." : "Add Test Person"}
          </button>
        </div>
      </section>
    </main>
  );
}
